import { useState, useEffect, useMemo } from "react";
import SiteDeactivationPanel, { type SelectableSite } from "./SiteDeactivationPanel";
import {
    Actions,
    PrimaryButton,
    BusyNote,
    ProgressBar,
    CardIntro,
} from "./ui/Form";
import api from "../api";

interface SiteRecord {
    name: string;
    subzone: string;
    location_name: string;
    site_type: string;
    total_inspections: number;
    last_inspection: string;
    pointlocid: string;
}

interface AnalysisResult {
    sites: SiteRecord[];
    summary: {
        total_active_sites: number;
        sites_with_inspections: number;
        consecutive_dry_sites?: number;
        no_treatment_sites?: number;
        date_range: string;
        treatment_activities_checked?: string[];
    };
}

/** A site that went inactive recently, which the ledger may not know about. */
interface Candidate {
    global_id: string;
    name: string;
    subzone: string;
    by: string;
    at: string;
    at_iso: string;
    symbology: string;
    has_note: boolean;
    reason: string;
    in_ledger: boolean;
}

interface Retirement {
    global_id: string;
    name: string;
    subzone: string;
    habitat: string;
    by: string;
    by_name: string;
    initials: string;
    at: string;
    reason: string;
    symbology: string;
    /** `pioneer` means this tool's own ledger recorded the retirement.
     *  `note` means it was found by the old COMMENTS scan instead, which is
     *  all that exists for retirements made before the ledger. */
    source?: "pioneer" | "note" | "adopted";
    /** False where the COMMENTS field had no room for the note. Those are
     *  precisely the retirements the old list could never show. */
    note_written?: boolean;
    still_inactive?: boolean | null;
}

interface Visit {
    condition: string;
    date: string;
}

interface DryHistorySite {
    name: string;
    subzone: string;
    location_name: string;
    site_type: string;
    pointlocid: string;
    // Every inspection that year, in date order. A year reported as one
    // condition hid whether it was four dry visits or two dry and two wet.
    year_conditions: Record<string, Visit[] | null>;
    // Dry inspections, not dry years. Summarising a year to one condition
    // is what made this misleading: the question is whether a site has ever
    // come up dry, so a single dry visit qualifies it.
    dry_visits: number;
    years_with_dry: number;
    total_visits: number;
    dry_years_count: number;
    has_any_dry: boolean;
}

interface DryHistoryResult {
    sites: DryHistorySite[];
    years: number[];
    summary: {
        total_active_sites: number;
        sites_with_any_dry: number;
        subzone: string | null;
    };
}

// All known activities from CSV analysis
const ALL_ACTIVITIES = [
    '1st Notice', 'Adulticide Treatment', 'Assisted', 'Barrier Treatment',
    'Final Notice', 'Fish Plant', 'Herbicide Treatment', 'Inspection',
    'Larvicide Treatment', 'No Contact', 'Physical Control', 'Pre-treatment',
    'Second Notice', 'Seining/Transport', 'Spoke to Resident',
    'Started Irrigation', 'Tagged', 'Treat/Fish', 'UAS/Drone Assist',
    'Verification Complete', 'Verification Pending', 'Voicemail',
    'Warrant Assist', 'Warrant Fish Plant', 'Warrant Inspection',
    'Warrant Larvicide', 'Warrant Phys', 'Warrant Pre-treatment',
    'Warrant Treat/Fish'
];

// Default treatment activities (pre-checked)
const DEFAULT_TREATMENT_ACTIVITIES = [
    'Adulticide Treatment', 'Barrier Treatment', 'Fish Plant',
    'Herbicide Treatment', 'Larvicide Treatment', 'Physical Control',
    'Pre-treatment', 'Seining/Transport', 'Started Irrigation', 'Treat/Fish',
    'Warrant Fish Plant', 'Warrant Larvicide', 'Warrant Phys',
    'Warrant Pre-treatment', 'Warrant Treat/Fish'
];

/** Return Tailwind classes for a condition cell based on its value. */
function conditionCellClass(cond: string | null): string {
    if (!cond) return 'bg-gray-50 text-gray-300';
    switch (cond) {
        case 'Dry':                   return 'bg-amber-100 text-amber-800 font-semibold';
        case 'Clear/Fresh':           return 'bg-sky-100 text-sky-800';
        case 'Unknown':               return 'bg-gray-100 text-gray-500';
        case 'Dark Green':            return 'bg-green-200 text-green-900';
        case 'Dark Green w/ Debris':  return 'bg-green-200 text-green-900';
        case 'Light Green/Cloudy':    return 'bg-green-100 text-green-700';
        case 'Covered':               return 'bg-blue-100 text-blue-700';
        case 'No Source':             return 'bg-red-100 text-red-700';
        default:                      return 'bg-gray-50 text-gray-600';
    }
}

/** Abbreviate long condition labels for narrow table cells. */
function conditionLabel(cond: string | null): string {
    if (!cond) return '—';
    const abbr: Record<string, string> = {
        'Dark Green w/ Debris': 'DG+Debris',
        'Light Green/Cloudy':   'Lt Green',
        'Clear/Fresh':          'Clear',
        'No Source':            'No Src',
    };
    return abbr[cond] ?? cond;
}

const CURRENT_YEAR = new Date().getFullYear();
// Back to 2010 and up to this year. The old list stopped at last year, so
// 2026 could not be chosen at all and every preset ended in 2025.
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2009 }, (_, i) => CURRENT_YEAR - i);

export default function SiteReviewCard() {
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [reportType, setReportType] = useState<'no_treatment' | 'dry_history'>('dry_history');
    const [selectedActivities, setSelectedActivities] = useState<string[]>(DEFAULT_TREATMENT_ACTIVITIES);
    const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [sortField, setSortField] = useState<keyof SiteRecord>('subzone');
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [filterSubzone, setFilterSubzone] = useState<string>('');

    // B3: sites ticked for retirement. Keyed by pointlocid so a site selected
    // in one report stays selected if the other report lists it too.
    const [picked, setPicked] = useState<Record<string, SelectableSite>>({});
    const pickedList = Object.values(picked);
    const toggle = (site: SelectableSite) => setPicked(prev => {
        const next = { ...prev };
        if (next[site.pointlocid]) delete next[site.pointlocid];
        else next[site.pointlocid] = site;
        return next;
    });
    /** Tick or untick everything currently shown.
     *
     *  Scoped to the filtered rows rather than the whole result, because the
     *  filter is how a supervisor narrows to the set they mean -- selecting
     *  sites they have filtered out would be the opposite of helpful. The
     *  deactivation panel still previews and confirms before anything is
     *  written, so a select-all cannot retire a list by itself.
     */
    const toggleAll = (sites: SelectableSite[], on: boolean) => setPicked(prev => {
        const next = { ...prev };
        for (const s of sites) {
            if (on) next[s.pointlocid] = s;
            else delete next[s.pointlocid];
        }
        return next;
    });

    const dropPicked = (ids: string[]) => setPicked(prev => {
        const next = { ...prev };
        // AGOL hands GlobalIDs back braced and upper-cased; match loosely.
        const gone = new Set(ids.map(i => i.replace(/[{}]/g, '').toUpperCase()));
        for (const k of Object.keys(next)) {
            if (gone.has(k.replace(/[{}]/g, '').toUpperCase())) delete next[k];
        }
        return next;
    });

    // Dry history state
    const [startYear, setStartYear] = useState<number>(CURRENT_YEAR - 5);
    const [endYear, setEndYear] = useState<number>(CURRENT_YEAR);
    const [dryResult, setDryResult] = useState<DryHistoryResult | null>(null);
    const [dryFilterSubzone, setDryFilterSubzone] = useState<string>('');

    // Subzone filter (pre-analysis scope limiter)
    // Looking one site up by name is how a supervisor usually arrives here:
    // someone asks about 132118-750 and its subzone is not the way in.
    const [siteName, setSiteName] = useState<string>('');
    // Who retired what. AGOL recorded it all along; nothing could read it
    // back, so the question needed opening features one at a time.
    const [retirements, setRetirements] = useState<Retirement[] | null>(null);
    const [retireDays, setRetireDays] = useState<number>(180);
    const [loadingRetirements, setLoadingRetirements] = useState(false);
    const [selectedSubzone, setSelectedSubzone] = useState<string>('');
    const [availableSubzones, setAvailableSubzones] = useState<string[]>([]);

    // Diagnostic state

    // Set default dates for consecutive dry (3 years) or no treatment (5 years)
    useEffect(() => {
        const now = new Date();
        const endYear = now.getFullYear(); // Include the year in progress
        setEndDate(`${endYear}-12-31`);

        if (reportType === 'no_treatment') {
            setStartDate(`${endYear - 4}-01-01`); // 5 years back
        }
    }, [reportType]);

    // Auto-load subzones silently on mount so the dropdown is pre-populated
    useEffect(() => {
        api.post("/discover-site-fields", {})
            .then(resp => {
                if (resp.data.data?.subzone_values?.length) {
                    setAvailableSubzones(resp.data.data.subzone_values);
                }
            })
            .catch(() => { /* silent — an empty dropdown is enough of a signal */ });
    }, []);

    const loadRetirements = async (days: number) => {
        setLoadingRetirements(true);
        try {
            const r = await api.get(`/site-deactivations?days=${days}&_=${Date.now()}`);
            setRetirements(r.data.rows || []);
        } catch {
            setRetirements([]);
        } finally {
            setLoadingRetirements(false);
        }
    };

    /** Column sorting for the retirement list. Default is newest first,
     *  which is what the question "what just happened" wants; site name and
     *  who retired it are the two people actually look things up by. */
    const [retireSort, setRetireSort] = useState<{ key: 'name' | 'subzone' | 'by' | 'at' | 'reason'; dir: 1 | -1 }>(
        { key: 'at', dir: -1 }
    );

    const sortedRetirements: Retirement[] = useMemo(() => {
        const rows = [...(retirements ?? [])];
        const { key, dir } = retireSort;
        const read = (r: Retirement) =>
            key === 'by' ? (r.by_name || r.by || '') : String((r as any)[key] ?? '');
        return rows.sort((a, b) => read(a).localeCompare(read(b), undefined, { numeric: true }) * dir);
    }, [retirements, retireSort]);

    const toggleRetireSort = (key: typeof retireSort.key) =>
        setRetireSort(prev => (prev.key === key ? { key, dir: prev.dir === 1 ? -1 : 1 } : { key, dir: 1 }));

    /** Recovering retirements the ledger missed. Needed because the ledger
     *  only knows what Pioneer wrote after it existed, and the note it used
     *  to rely on is written only when it fits in COMMENTS. */
    const [candidates, setCandidates] = useState<Candidate[] | null>(null);
    const [candidateDays, setCandidateDays] = useState(7);
    const [findingCandidates, setFindingCandidates] = useState(false);
    const [adopting, setAdopting] = useState(false);
    const [chosen, setChosen] = useState<Record<string, boolean>>({});

    const findCandidates = async () => {
        setFindingCandidates(true);
        try {
            const r = await api.get(`/site-retirement-candidates?days=${candidateDays}&_=${Date.now()}`);
            const rows: Candidate[] = r.data.rows || [];
            setCandidates(rows);
            // Anything carrying Pioneer's note is almost certainly ours, so
            // it starts ticked. The rest is a judgement call and does not.
            setChosen(Object.fromEntries(
                rows.filter(c => !c.in_ledger && c.has_note).map(c => [c.global_id, true])
            ));
        } catch {
            setCandidates([]);
        } finally {
            setFindingCandidates(false);
        }
    };

    const adoptChosen = async () => {
        const sites = (candidates ?? []).filter(c => chosen[c.global_id]);
        if (!sites.length) return;
        setAdopting(true);
        try {
            await api.post('/site-retirement-adopt', { sites });
            await loadRetirements(retireDays);
            setCandidates(null);
            setChosen({});
        } finally {
            setAdopting(false);
        }
    };

    const [reviving, setReviving] = useState<Record<string, boolean>>({});
    const [reviveNote, setReviveNote] = useState<string | null>(null);

    /** Undo a retirement: ACTIVE back on, map status back to active, and
     *  Pioneer's note removed so the site stops showing as retired here. */
    const reactivate = async (r: Retirement) => {
        setReviving(p => ({ ...p, [r.global_id]: true }));
        setReviveNote(null);
        try {
            await api.post('/reactivate-sites', { global_ids: [r.global_id], confirm: true });
            setRetirements(prev => (prev || []).filter(x => x.global_id !== r.global_id));
            setReviveNote(`${r.name} is active again.`);
        } catch (e: any) {
            setReviveNote(e?.response?.data?.message || `Could not reactivate ${r.name}.`);
        } finally {
            setReviving(p => ({ ...p, [r.global_id]: false }));
        }
    };

    const downloadRetirements = () => {
        if (!retirements?.length) return;
        const headers = ['Site Name', 'SubZone', 'Habitat', 'Retired By', 'Initials', 'When', 'Reason'];
        const rows = retirements.map(r => [r.name, r.subzone, r.habitat, r.by_name || r.by, r.initials, r.at, r.reason]);
        const csv = [headers, ...rows]
            .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
            .join(String.fromCharCode(10));
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        link.download = `site_retirements_last_${retireDays}_days.csv`;
        link.click();
    };

    const handleActivityToggle = (activity: string) => {
        setSelectedActivities(prev =>
            prev.includes(activity)
                ? prev.filter(a => a !== activity)
                : [...prev, activity]
        );
    };

    const handleSelectAllActivities = () => setSelectedActivities([...ALL_ACTIVITIES]);
    const handleDeselectAllActivities = () => setSelectedActivities([]);
    const handleSelectTreatmentsOnly = () => setSelectedActivities([...DEFAULT_TREATMENT_ACTIVITIES]);






    const handleAnalyze = async () => {
        setIsAnalyzing(true);
        setResult(null);
        setDryResult(null);
        setError(null);

        try {
            if (reportType === 'dry_history') {
                if (startYear > endYear) {
                    setError("Start year must be before or equal to end year");
                    return;
                }
                const payload: any = {
                    start_year: startYear,
                    end_year:   endYear,
                    site_name:  siteName.trim() || undefined,
                    ...(selectedSubzone ? { subzone: selectedSubzone } : {})
                };
                const response = await api.post("/analyze-dry-history", payload);
                if (response.data.status === "success") {
                    setDryResult(response.data.data);
                } else {
                    setError(response.data.message || "Analysis failed");
                }
            } else {
                if (!startDate || !endDate) {
                    setError("Please select both start and end dates");
                    return;
                }
                const payload: any = {
                    report_type: reportType,
                    start_date: startDate,
                    end_date: endDate,
                    ...(selectedSubzone ? { subzone: selectedSubzone } : {})
                };
                if (reportType === 'no_treatment') {
                    payload.treatment_activities = selectedActivities;
                }
                const response = await api.post("/analyze-sites", payload);
                if (response.data.status === "success") {
                    setResult(response.data.data);
                } else {
                    setError(response.data.message || "Analysis failed");
                }
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || err.message || "Unknown error occurred";
            setError(msg);
        } finally {
            setIsAnalyzing(false);
        }
    };

    /** Download the dry history table as CSV directly from frontend data (no re-fetch). */
    const handleDownloadDryHistoryCSV = () => {
        if (!dryResult || dryResult.sites.length === 0) return;
        const { years } = dryResult;
        const headers = ['Site Name', 'SubZone', 'Location Name', 'Site Type',
            ...years.map(String), 'Dry Visits', 'Years With A Dry Visit', 'Total Visits',
            'Percent Dry'];
        const rows = dryDisplaySites.map(s => [
            s.name, s.subzone, s.location_name, s.site_type,
            // One cell per year, every visit comma separated, so the file
            // says the same thing the table does rather than a summary of it.
            ...years.map(y => {
                const visits = s.year_conditions[String(y)];
                return visits && visits.length
                    ? visits.map(v => (v.date ? `${v.date} ${v.condition}` : v.condition)).join(', ')
                    : '-';
            }),
            s.dry_visits,
            s.years_with_dry,
            s.total_visits,
            // Unformatted and unsuffixed: a spreadsheet should be able to
            // sort and filter this without stripping a per cent sign first.
            (() => {
                const pct = dryShare(s.dry_visits, s.total_visits);
                return pct === null ? '' : pct.toFixed(1);
            })(),
        ]);
        const csv = [headers, ...rows]
            .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const sub = selectedSubzone ? `_${selectedSubzone}` : '';
        link.download = `dry_history_${years[0]}_${years[years.length - 1]}${sub}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadCSV = async () => {
        if (!result || result.sites.length === 0) return;
        setIsDownloading(true);
        try {
            const payload: any = {
                report_type: reportType,
                start_date: startDate,
                end_date: endDate,
                ...(selectedSubzone ? { subzone: selectedSubzone } : {})
            };
            if (reportType === 'no_treatment') {
                payload.treatment_activities = selectedActivities;
            }
            const response = await api.post("/download-site-report", payload, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `site_review_${reportType}_${startDate}_to_${endDate}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            setError("Failed to download CSV. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    // Sorting for the no_treatment table
    const handleSort = (field: keyof SiteRecord) => {
        if (sortField === field) setSortAsc(!sortAsc);
        else { setSortField(field); setSortAsc(true); }
    };
    const getSortIcon = (field: keyof SiteRecord) => {
        if (sortField !== field) return '↕';
        return sortAsc ? '↑' : '↓';
    };

    /** Sorting for the dry history table. Its own state rather than shared
     *  with the no-treatment table: the two tables are on screen together
     *  and hold different columns, so one sort setting would mean clicking
     *  a column in one silently reorders the other. */
    type DrySortKey = 'name' | 'subzone' | 'location_name' | 'site_type'
        | 'dry_visits' | 'percent_dry';
    const [drySortField, setDrySortField] = useState<DrySortKey>('percent_dry');
    const [drySortAsc, setDrySortAsc] = useState(false);

    const handleDrySort = (field: DrySortKey) => {
        if (drySortField === field) setDrySortAsc(!drySortAsc);
        // Counts and shares open on the largest, names on the first letter.
        // The interesting end of a number column is the top.
        else { setDrySortField(field); setDrySortAsc(field === 'name' || field === 'subzone' || field === 'location_name' || field === 'site_type'); }
    };
    const getDrySortIcon = (field: DrySortKey) => {
        if (drySortField !== field) return '\u2195';
        return drySortAsc ? '\u2191' : '\u2193';
    };

    const uniqueSubzones = result
        ? Array.from(new Set(result.sites.map(s => s.subzone))).sort()
        : [];

    const displaySites = result
        ? result.sites
            .filter(s => !filterSubzone || s.subzone === filterSubzone)
            .sort((a, b) => {
                const aVal = a[sortField], bVal = b[sortField];
                if (typeof aVal === 'number' && typeof bVal === 'number')
                    return sortAsc ? aVal - bVal : bVal - aVal;
                return sortAsc
                    ? String(aVal || '').localeCompare(String(bVal || ''))
                    : String(bVal || '').localeCompare(String(aVal || ''));
            })
        : [];

    const subzoneCounts = result
        ? result.sites.reduce((acc, site) => {
            acc[site.subzone] = (acc[site.subzone] || 0) + 1;
            return acc;
        }, {} as Record<string, number>)
        : {};

    const reportCount = result?.summary.no_treatment_sites;

    /** Share of a site's inspections that found it dry.
     *
     *  Null rather than zero when nothing was inspected: a site with no
     *  visits is unknown, not 0% dry, and the two would sort together.
     */
    const dryShare = (dry: number, total: number): number | null =>
        total > 0 ? (dry / total) * 100 : null;

    // Dry history derived data
    const dryUniqueSubzones = dryResult
        ? Array.from(new Set(dryResult.sites.map(s => s.subzone))).sort()
        : [];
    const dryDisplaySites = dryResult
        ? dryResult.sites
            .filter(s => !dryFilterSubzone || s.subzone === dryFilterSubzone)
            .slice()
            .sort((a, b) => {
                if (drySortField === 'percent_dry' || drySortField === 'dry_visits') {
                    const read = (x: typeof a) => drySortField === 'dry_visits'
                        ? x.dry_visits
                        // A site with no inspections sorts below every real
                        // share rather than alongside 0%: it is unknown, not
                        // never dry, and the table says so with a dash.
                        : (dryShare(x.dry_visits, x.total_visits) ?? -1);
                    return drySortAsc ? read(a) - read(b) : read(b) - read(a);
                }
                const aVal = String(a[drySortField] || '');
                const bVal = String(b[drySortField] || '');
                return drySortAsc
                    ? aVal.localeCompare(bVal, undefined, { numeric: true })
                    : bVal.localeCompare(aVal, undefined, { numeric: true });
            })
        : [];
    const drySubzoneCounts = dryResult
        ? dryResult.sites.reduce((acc, s) => {
            acc[s.subzone] = (acc[s.subzone] || 0) + 1;
            return acc;
        }, {} as Record<string, number>)
        : {};

    const analyzeDisabled = isAnalyzing || (
        reportType !== 'dry_history' && (!startDate || !endDate)
    );

    return (
                <div className="space-y-4">
            <CardIntro>
                Finds active monitoring sites that may no longer need monitoring.
                Excludes pools, spas and doughboys. Results are grouped by subzone.
            </CardIntro>


            {/* Report Type Selection */}
            <div className="mb-6">
                <label className="eyebrow block mb-2">Report Type</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                        onClick={() => { setReportType('dry_history'); setResult(null); setDryResult(null); setError(null); }}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${reportType === 'dry_history'
                            ? 'border-brand bg-brand-tint shadow-md'
                            : 'border-line hover:border-line-strong bg-card'}`}
                    >
                        <div className="font-medium text-gray-800">📅 Dry History by Year</div>
                        <div className="text-xs text-gray-500 mt-1">
                            Per-year condition breakdown — spot sites that are typically dry across multiple years
                        </div>
                    </button>
                    <button
                        onClick={() => { setReportType('no_treatment'); setResult(null); setDryResult(null); setError(null); }}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${reportType === 'no_treatment'
                            ? 'border-brand bg-brand-tint shadow-md'
                            : 'border-line hover:border-line-strong bg-card'}`}
                    >
                        <div className="font-medium text-gray-800">🚫 No Treatment Sites</div>
                        <div className="text-xs text-gray-500 mt-1">
                            Sites with ZERO selected treatment activities in the date range
                        </div>
                    </button>
                </div>
            </div>

            {/* Year Range (dry_history only) */}
            {reportType === 'dry_history' && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Year Range</label>
                        <div className="flex gap-1.5">
                            {[
                                { label: 'Last 3 yrs', start: CURRENT_YEAR - 3 + 1, end: CURRENT_YEAR },
                                { label: 'Last 5 yrs', start: CURRENT_YEAR - 5 + 1, end: CURRENT_YEAR },
                                { label: 'Last 7 yrs', start: CURRENT_YEAR - 7 + 1, end: CURRENT_YEAR },
                            ].map(({ label, start, end }) => (
                                <button
                                    key={label}
                                    onClick={() => { setStartYear(start); setEndYear(end); setDryResult(null); setError(null); }}
                                    className="text-xs px-2 py-1 bg-gray-100 text-gray-600 border border-gray-200 rounded hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300 transition-colors"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Start Year</label>
                            <select
                                value={startYear}
                                onChange={e => { setStartYear(Number(e.target.value)); setDryResult(null); setError(null); }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-sm"
                            >
                                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">End Year</label>
                            <select
                                value={endYear}
                                onChange={e => { setEndYear(Number(e.target.value)); setDryResult(null); setError(null); }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white text-sm"
                            >
                                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>
                    {startYear > endYear && (
                        <p className="text-xs text-red-600 mt-1">Start year must be before or equal to end year.</p>
                    )}
                    {endYear - startYear > 9 && (
                        <p className="text-xs text-red-600 mt-1">Maximum range is 10 years.</p>
                    )}
                </div>
            )}

            {/* Date Range (no_treatment only) */}
            {reportType !== 'dry_history' && (
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Date Range</label>
                        <div className="flex gap-1.5">
                            {[
                                { label: '30 days', days: 30 },
                                { label: '90 days', days: 90 },
                                { label: '1 year', days: 365 },
                                { label: '3 years', days: 1095 },
                            ].map(({ label, days }) => (
                                <button
                                    key={label}
                                    onClick={() => {
                                        const end = new Date();
                                        end.setDate(end.getDate() - 1);
                                        const start = new Date(end);
                                        start.setDate(start.getDate() - days);
                                        setStartDate(start.toISOString().split('T')[0]);
                                        setEndDate(end.toISOString().split('T')[0]);
                                        setResult(null); setError(null);
                                    }}
                                    className="text-xs px-2 py-1 bg-gray-100 text-gray-600 border border-gray-200 rounded hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 transition-colors"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">End Date</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                    </div>
                </div>
            )}

            {/* Subzone Filter */}
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <div className="flex-1">
                        <label className="eyebrow block mb-1">
                            Subzone Filter
                            <span className="ml-2 text-xs font-normal text-gray-500">
                                {selectedSubzone ? '— targeted query (faster)' : '— all subzones (slower for wide ranges)'}
                            </span>
                        </label>
                        <select
                            value={selectedSubzone}
                            onChange={e => { setSelectedSubzone(e.target.value); setResult(null); setDryResult(null); setError(null); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        >
                            <option value="">All Subzones (district-wide)</option>
                            {availableSubzones.map(z => <option key={z} value={z}>{z}</option>)}
                        </select>
                        {availableSubzones.length === 0 && (
                            <p className="text-xs text-gray-400 mt-1">Subzones load automatically; reload the page if this stays empty.</p>
                        )}
                    </div>
                    {selectedSubzone && (
                        <button
                            onClick={() => { setSelectedSubzone(''); setResult(null); setDryResult(null); setError(null); }}
                            className="mt-5 text-xs px-2 py-1.5 text-gray-500 hover:text-red-600 border border-gray-200 rounded hover:border-red-300 transition-colors"
                        >
                            ✕ Clear
                        </button>
                    )}
                </div>
                {selectedSubzone && (
                    <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 mt-2">
                        Subzone mode: querying only sites in <strong>{selectedSubzone}</strong>.
                    </p>
                )}

                {/* Looking one site up by name is how a supervisor usually
                    arrives here -- someone asks about 132118-750, and its
                    subzone is not the way in. */}
                <div className="mt-3">
                    <label className="eyebrow block mb-1">
                        Or find one site by name
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            — shows its history whether or not it has ever been dry
                        </span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            value={siteName}
                            onChange={e => { setSiteName(e.target.value); setResult(null); setDryResult(null); setError(null); }}
                            placeholder="e.g. 132118-750"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        />
                        {siteName && (
                            <button
                                onClick={() => { setSiteName(''); setResult(null); setDryResult(null); setError(null); }}
                                className="text-xs px-2 py-1.5 text-gray-500 hover:text-red-600 border border-gray-200 rounded hover:border-red-300 transition-colors"
                            >
                                ✕ Clear
                            </button>
                        )}
                    </div>
                    {siteName.trim() && (
                        <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 mt-2">
                            Name search: subzone is ignored while this is set.
                        </p>
                    )}
                </div>
            </div>

            {/* Activity Checkboxes (no_treatment only) */}
            {reportType === 'no_treatment' && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                            Activity Types to Count as "Treatment" ({selectedActivities.length} selected)
                        </label>
                        <div className="flex gap-2">
                            <button onClick={handleSelectTreatmentsOnly} className="text-xs px-2 py-1 bg-brand-tint text-brand-deep rounded-sm2 hover:bg-brand hover:text-white transition">Default Treatments</button>
                            <button onClick={handleSelectAllActivities} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Select All</button>
                            <button onClick={handleDeselectAllActivities} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Deselect All</button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {ALL_ACTIVITIES.map(activity => (
                            <label key={activity} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                <input type="checkbox" checked={selectedActivities.includes(activity)}
                                    onChange={() => handleActivityToggle(activity)}
                                    className="rounded text-teal-600 focus:ring-teal-500" />
                                <span className={selectedActivities.includes(activity) ? 'text-gray-800 font-medium' : 'text-gray-500'}>
                                    {activity}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

                        <Actions>
                <PrimaryButton
                    onClick={handleAnalyze}
                    busy={isAnalyzing}
                    busyLabel="Analyzing…"
                    disabled={analyzeDisabled || startYear > endYear || endYear - startYear > 9}
                >
                    Analyze sites
                </PrimaryButton>
            </Actions>

            {isAnalyzing && <ProgressBar />}
            {isAnalyzing && (
                <BusyNote>
                    Querying PointLocation and the inspection layer.
                    {reportType === 'dry_history' &&
                        ` One query per year (${endYear - startYear + 1} years) — this can take a few minutes.`}
                </BusyNote>
            )}

            {error && (
                <div className="rounded-sm2 bg-red-50 border border-red-200 p-3">
                    <p className="text-red-800 font-semibold text-sm">Analysis failed</p>
                    <p className="text-red-700 text-[13px] mt-0.5">{error}</p>
                </div>
            )}

            {/* ── DRY HISTORY RESULTS ── */}
            {dryResult && (
                <div className="mt-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-blue-600">Active Sites</p>
                            <p className="text-xl font-bold text-blue-800">{dryResult.summary.total_active_sites.toLocaleString()}</p>
                        </div>
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                            <p className="text-xs text-amber-600">Sites ever recorded dry</p>
                            <p className="text-xl font-bold text-amber-800">{dryResult.summary.sites_with_any_dry.toLocaleString()}</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                            <p className="text-xs text-purple-600">SubZones</p>
                            <p className="text-xl font-bold text-purple-800">{dryUniqueSubzones.length}</p>
                        </div>
                    </div>

                    {/* Condition legend */}
                    <div className="mb-3 flex flex-wrap gap-2 text-xs">
                        <span className="font-medium text-gray-600">Legend:</span>
                        {[
                            { label: 'Dry', cls: 'bg-amber-100 text-amber-800 font-semibold' },
                            { label: 'Clear/Fresh', cls: 'bg-sky-100 text-sky-800' },
                            { label: 'Dark Green', cls: 'bg-green-200 text-green-900' },
                            { label: 'Unknown', cls: 'bg-gray-100 text-gray-500' },
                            { label: 'No Source', cls: 'bg-red-100 text-red-700' },
                            { label: '— (no visit)', cls: 'bg-gray-50 text-gray-300' },
                        ].map(({ label, cls }) => (
                            <span key={label} className={`px-2 py-0.5 rounded ${cls}`}>{label}</span>
                        ))}
                    </div>

                    {/* SubZone distribution pills */}
                    {Object.keys(drySubzoneCounts).length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Sites per SubZone:</p>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(drySubzoneCounts)
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([zone, count]) => (
                                        <span
                                            key={zone}
                                            onClick={() => setDryFilterSubzone(dryFilterSubzone === zone ? '' : zone)}
                                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full cursor-pointer transition-colors ${
                                                dryFilterSubzone === zone
                                                    ? 'bg-brand text-white'
                                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-amber-50'
                                            }`}
                                        >
                                            {zone}: <strong>{count}</strong>
                                        </span>
                                    ))}
                            </div>
                            {dryFilterSubzone && (
                                <button onClick={() => setDryFilterSubzone('')}
                                    className="mt-2 text-xs text-amber-600 hover:text-amber-800 underline">
                                    Clear filter (showing {dryDisplaySites.length} of {dryResult.sites.length})
                                </button>
                            )}
                        </div>
                    )}

                    {/* Pivot table */}
                    {dryResult.sites.length > 0 ? (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-extrabold text-[15px]">
                                    {dryFilterSubzone ? `${dryFilterSubzone} — ` : ''}
                                    {dryDisplaySites.length} Sites · years {dryResult.years[0]}–{dryResult.years[dryResult.years.length - 1]}
                                </h3>
                                <button
                                    onClick={handleDownloadDryHistoryCSV}
                                    className="btn-primary text-sm inline-flex items-center gap-2"
                                >
                                    📥 Download CSV
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">
                                Condition shown is the most common for that year. "Dry" wins ties.
                                Use CSV for advanced filtering.
                            </p>
                            <div className="overflow-auto border border-gray-200 rounded-lg" style={{ maxHeight: '480px' }}>
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-100 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-2 py-2 w-8">
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all sites shown"
                                                    title="Select all sites shown"
                                                    checked={dryDisplaySites.length > 0 && dryDisplaySites.every(s => picked[s.pointlocid])}
                                                    ref={el => { if (el) el.indeterminate =
                                                        dryDisplaySites.some(s => picked[s.pointlocid]) &&
                                                        !dryDisplaySites.every(s => picked[s.pointlocid]); }}
                                                    onChange={e => toggleAll(dryDisplaySites.map(s => ({
                                                        pointlocid: s.pointlocid, name: s.name, subzone: s.subzone,
                                                        location_name: s.location_name,
                                                    })), e.target.checked)}
                                                />
                                            </th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleDrySort('name')}>Site Name {getDrySortIcon('name')}</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleDrySort('subzone')}>SubZone {getDrySortIcon('subzone')}</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleDrySort('location_name')}>Location {getDrySortIcon('location_name')}</th>
                                            <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleDrySort('site_type')}>Type {getDrySortIcon('site_type')}</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" title="Dry inspections as a share of all inspections at this site" onClick={() => handleDrySort('percent_dry')}>% Dry {getDrySortIcon('percent_dry')}</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 select-none" title="Dry inspections, and how many separate years they fall across" onClick={() => handleDrySort('dry_visits')}>Dry {getDrySortIcon('dry_visits')}</th>
                                            {dryResult.years.map(y => (
                                                <th key={y} className="px-2 py-2 text-center font-medium text-gray-700 whitespace-nowrap">{y}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dryDisplaySites.map((site, idx) => (
                                            <tr key={idx} className="border-t hover:bg-gray-50">
                                                <td className="px-2 py-1.5 text-center">
                                                    <input type="checkbox" checked={!!picked[site.pointlocid]}
                                                        onChange={() => toggle({ pointlocid: site.pointlocid, name: site.name, subzone: site.subzone, location_name: site.location_name })} />
                                                </td>
                                                <td className="px-3 py-1.5 font-mono font-medium text-gray-800 whitespace-nowrap">{site.name || '—'}</td>
                                                <td className="px-3 py-1.5 font-medium text-indigo-700 whitespace-nowrap">{site.subzone}</td>
                                                <td className="px-3 py-1.5 text-gray-600 max-w-xs truncate">{site.location_name || '—'}</td>
                                                <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{site.site_type || '—'}</td>
                                                <td className="px-3 py-1.5 text-center whitespace-nowrap">
                                                    {(() => {
                                                        const pct = dryShare(site.dry_visits, site.total_visits);
                                                        if (pct === null) {
                                                            return <span className="text-gray-300">—</span>;
                                                        }
                                                        // Whole numbers except near the ends, where 99% and 100%
                                                        // are a different thing to a supervisor and rounding
                                                        // 99.4 up to 100 would say "always dry" of a site that
                                                        // was not.
                                                        const shown = pct > 0 && pct < 1 ? pct.toFixed(1)
                                                            : pct > 99 && pct < 100 ? pct.toFixed(1)
                                                                : Math.round(pct).toString();
                                                        return (
                                                            <span className={pct >= 80 ? 'font-bold text-amber-700' : 'text-gray-700'}>
                                                                {shown}%
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="px-3 py-1.5 text-center whitespace-nowrap">
                                                    <span className="font-bold text-amber-700">{site.dry_visits}</span>
                                                    <span className="text-gray-400 text-xs"> of {site.total_visits}</span>
                                                    <div className="text-[10px] text-gray-400">
                                                        {site.years_with_dry} yr{site.years_with_dry === 1 ? '' : 's'}
                                                    </div>
                                                </td>
                                                {dryResult.years.map(y => {
                                                    const visits = site.year_conditions[String(y)];
                                                    return (
                                                        <td key={y} className="px-1 py-1.5 align-top">
                                                            {!visits || visits.length === 0 ? (
                                                                <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-gray-50 text-gray-300">—</span>
                                                            ) : (
                                                                <div className="flex flex-col gap-0.5 items-stretch">
                                                                    {visits.map((v, i) => (
                                                                        <span
                                                                            key={i}
                                                                            title={v.date ? `${v.date}: ${v.condition}` : v.condition}
                                                                            className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap text-center ${conditionCellClass(v.condition)}`}
                                                                        >
                                                                            {conditionLabel(v.condition)}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-700 font-medium">No sites had a predominantly dry year in the selected range.</p>
                        </div>
                    )}
                </div>
            )}

            {/* ── CONSECUTIVE DRY / NO TREATMENT RESULTS ── */}
            {result && (
                <div className="mt-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <p className="text-xs text-blue-600">Active Sites</p>
                            <p className="text-xl font-bold text-blue-800">{result.summary.total_active_sites.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                            <p className="text-xs text-gray-600">With Inspections</p>
                            <p className="text-xl font-bold text-gray-800">{result.summary.sites_with_inspections.toLocaleString()}</p>
                        </div>
                        <div className={`p-3 rounded-lg border ${(reportCount || 0) > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                            <p className={`text-xs ${(reportCount || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                No Treatment
                            </p>
                            <p className={`text-xl font-bold ${(reportCount || 0) > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                                {(reportCount || 0).toLocaleString()}
                            </p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                            <p className="text-xs text-purple-600">SubZones</p>
                            <p className="text-xl font-bold text-purple-800">{uniqueSubzones.length}</p>
                        </div>
                    </div>

                    {Object.keys(subzoneCounts).length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-2">Sites per SubZone:</p>
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(subzoneCounts).sort(([a], [b]) => a.localeCompare(b)).map(([zone, count]) => (
                                    <span key={zone}
                                        onClick={() => setFilterSubzone(filterSubzone === zone ? '' : zone)}
                                        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full cursor-pointer transition-colors ${
                                            filterSubzone === zone
                                                ? 'bg-brand text-white'
                                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50'
                                        }`}>
                                        {zone}: <strong>{count}</strong>
                                    </span>
                                ))}
                            </div>
                            {filterSubzone && (
                                <button onClick={() => setFilterSubzone('')}
                                    className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 underline">
                                    Clear filter (showing {displaySites.length} of {result.sites.length})
                                </button>
                            )}
                        </div>
                    )}

                    {result.sites.length > 0 ? (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-extrabold text-[15px]">
                                    {filterSubzone ? `${filterSubzone} — ` : ''}{displaySites.length} Sites
                                </h3>
                                <button onClick={handleDownloadCSV} disabled={isDownloading}
                                    className="btn-primary text-sm inline-flex items-center gap-2">
                                    {isDownloading ? '⏳ Downloading...' : '📥 Download CSV'}
                                </button>
                            </div>
                            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-100 sticky top-0">
                                        <tr>
                                            <th className="px-2 py-2 w-8">
                                                <input
                                                    type="checkbox"
                                                    aria-label="Select all sites shown"
                                                    title="Select all sites shown"
                                                    checked={displaySites.length > 0 && displaySites.every(s => picked[s.pointlocid])}
                                                    ref={el => { if (el) el.indeterminate =
                                                        displaySites.some(s => picked[s.pointlocid]) &&
                                                        !displaySites.every(s => picked[s.pointlocid]); }}
                                                    onChange={e => toggleAll(displaySites.map(s => ({
                                                        pointlocid: s.pointlocid, name: s.name, subzone: s.subzone,
                                                        location_name: s.location_name,
                                                    })), e.target.checked)}
                                                />
                                            </th>
                                            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('name')}>Site Name {getSortIcon('name')}</th>
                                            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('subzone')}>SubZone {getSortIcon('subzone')}</th>
                                            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('location_name')}>Location Name {getSortIcon('location_name')}</th>
                                            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('site_type')}>Site Type {getSortIcon('site_type')}</th>
                                            <th className="px-3 py-2 text-center cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('total_inspections')}>Inspections {getSortIcon('total_inspections')}</th>
                                            <th className="px-3 py-2 text-left cursor-pointer hover:bg-gray-200 select-none" onClick={() => handleSort('last_inspection')}>Last Inspection {getSortIcon('last_inspection')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displaySites.map((site, index) => (
                                            <tr key={index} className="border-t hover:bg-gray-50">
                                                <td className="px-2 py-2 text-center">
                                                    <input type="checkbox" checked={!!picked[site.pointlocid]}
                                                        onChange={() => toggle({ pointlocid: site.pointlocid, name: site.name, subzone: site.subzone, location_name: site.location_name, last_inspection: site.last_inspection })} />
                                                </td>
                                                <td className="px-3 py-2 font-mono text-sm font-medium text-gray-800">{site.name || '—'}</td>
                                                <td className="px-3 py-2 font-medium text-indigo-700">{site.subzone}</td>
                                                <td className="px-3 py-2">{site.location_name || '—'}</td>
                                                <td className="px-3 py-2 text-gray-600">{site.site_type || '—'}</td>
                                                <td className="px-3 py-2 text-center">{site.total_inspections}</td>
                                                <td className="px-3 py-2 text-gray-600">{site.last_inspection || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-700 font-medium">No sites match the criteria for the selected date range.</p>
                        </div>
                    )}
                </div>
            )}

            <div className="rounded-sm2 border border-line-strong p-3 text-[13px] space-y-1">
                <div className="font-semibold">How to read this</div>
                <p className="text-ink-soft">
                    <strong>Dry history</strong> lists every inspection year by year, and
                    shows any site recorded dry at least once. The count is dry
                    inspections out of total, with the number of separate years below —
                    four dry visits in one season is a different case from four spread
                    across four years.
                </p>
                <p className="text-ink-soft">
                    <strong>No treatment</strong> lists sites with none of the selected
                    treatment activities in the range.
                </p>
                <p className="text-ink-soft">
                    Narrow by subzone before analysing to keep long ranges quick, then
                    download the CSV.
                </p>
                <p className="text-ink-soft">
                    <strong>Tick sites</strong> in either table to send them out for field
                    verification, or to retire them once you are satisfied.
                </p>
            </div>

            <div className="ui-card p-4 mt-4">
                <div className="flex flex-wrap items-baseline gap-2">
                    <h3 className="font-extrabold text-[15px]">Retired through Pioneer</h3>
                    <select
                        value={retireDays}
                        onChange={e => setRetireDays(Number(e.target.value))}
                        className="text-xs px-2 py-1 border border-line-strong rounded-sm2 bg-card"
                    >
                        {[1, 7, 30, 90, 180, 365, 1095].map(d => (
                            <option key={d} value={d}>
                                {d === 1 ? 'today' : d === 7 ? 'last 7 days' : `last ${d} days`}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={() => loadRetirements(retireDays)}
                        disabled={loadingRetirements}
                        className="btn-ghost text-xs !py-1"
                    >
                        {loadingRetirements ? 'Reading…' : retirements ? 'Refresh' : 'Show'}
                    </button>
                    {!!retirements?.length && (
                        <button onClick={downloadRetirements} className="btn-ghost text-xs !py-1 ml-auto">
                            📥 CSV
                        </button>
                    )}
                </div>
                <p className="text-ink-soft text-[13px] mt-1">
                    Who retired which site, and why, from Pioneer's own record of what it
                    wrote. Rows marked <em>from note</em> predate that record and were found
                    by the note left in COMMENTS instead — for those, the name is whoever
                    edited the feature last, which is not necessarily who retired it.
                </p>
                {reviveNote && (
                    <p className="text-[13px] text-brand-deep mt-2 font-semibold">{reviveNote}</p>
                )}

                <div className="mt-3 border-t border-line pt-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] text-ink-soft">
                            Missing a retirement you know happened?
                        </span>
                        <select
                            value={candidateDays}
                            onChange={e => setCandidateDays(Number(e.target.value))}
                            className="text-xs px-2 py-1 border border-line-strong rounded-sm2 bg-card"
                        >
                            {[1, 7, 30, 90].map(d => (
                                <option key={d} value={d}>{d === 1 ? 'today' : `last ${d} days`}</option>
                            ))}
                        </select>
                        <button
                            onClick={findCandidates}
                            disabled={findingCandidates}
                            className="btn-ghost text-xs !py-1"
                        >
                            {findingCandidates ? 'Looking…' : 'Find it in the layer'}
                        </button>
                    </div>
                    <p className="text-[12px] text-ink-faint mt-1">
                        Lists every site that went inactive in that window, not just ones
                        carrying Pioneer's note, since a retirement whose note did not fit in
                        COMMENTS cannot be found by searching for one. Tick the ones this tool
                        made and they join the list above.
                    </p>

                    {candidates && candidates.length === 0 && (
                        <p className="text-[13px] text-ink-faint mt-2">
                            Nothing went inactive in that window.
                        </p>
                    )}

                    {!!candidates?.length && (
                        <>
                            <div className="overflow-auto max-h-64 border border-line rounded-sm2 mt-2">
                                <table className="w-full text-[12px]">
                                    <thead className="bg-paper-2 sticky top-0">
                                        <tr>
                                            {['', 'Site', 'SubZone', 'Last edited by', 'When', 'Pioneer note'].map(h => (
                                                <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {candidates.map(c => (
                                            <tr key={c.global_id} className="border-t border-line">
                                                <td className="px-2 py-1">
                                                    <input
                                                        type="checkbox"
                                                        disabled={c.in_ledger}
                                                        checked={!!chosen[c.global_id]}
                                                        onChange={e => setChosen(p => ({ ...p, [c.global_id]: e.target.checked }))}
                                                    />
                                                </td>
                                                <td className="px-2 py-1 font-mono">{c.name || '—'}</td>
                                                <td className="px-2 py-1">{c.subzone || '—'}</td>
                                                <td className="px-2 py-1 text-ink-faint">{c.by || '—'}</td>
                                                <td className="px-2 py-1 whitespace-nowrap text-ink-faint">{c.at || '—'}</td>
                                                <td className="px-2 py-1">
                                                    {c.in_ledger ? (
                                                        <span className="pill bg-brand-tint text-brand-deep">already listed</span>
                                                    ) : c.has_note ? (
                                                        <span className="pill bg-brand-tint text-brand-deep">yes</span>
                                                    ) : (
                                                        <span className="pill bg-sle-tint text-sle" title="Retired without Pioneer's note — most likely because COMMENTS had no room">
                                                            no note
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                onClick={adoptChosen}
                                disabled={adopting || !Object.values(chosen).some(Boolean)}
                                className="btn-ghost text-xs !py-1 mt-2"
                            >
                                {adopting ? 'Adding…' : `Add ${Object.values(chosen).filter(Boolean).length} to the list`}
                            </button>
                            <p className="text-[12px] text-ink-faint mt-1">
                                Added rows are credited to the account ArcGIS recorded as the
                                last editor and marked <em>recovered</em>, since this is
                                reconstructed after the fact rather than something Pioneer
                                watched happen.
                            </p>
                        </>
                    )}
                </div>
                {retirements && retirements.length === 0 && !loadingRetirements && (
                    <p className="text-[13px] text-ink-faint mt-2">
                        Nothing retired through Pioneer in that window.
                    </p>
                )}
                {!!retirements?.length && (
                    <div className="overflow-auto max-h-80 border border-line rounded-sm2 mt-3">
                        <table className="w-full text-[12px]">
                            <thead className="bg-paper-2 sticky top-0">
                                <tr>
                                    {([
                                        ['Site', 'name'],
                                        ['SubZone', 'subzone'],
                                        ['Retired by', 'by'],
                                        ['When', 'at'],
                                        ['Reason', 'reason'],
                                    ] as const).map(([label, key]) => (
                                        <th
                                            key={key}
                                            onClick={() => toggleRetireSort(key)}
                                            title={`Sort by ${label.toLowerCase()}`}
                                            className="px-2 py-1.5 text-left font-semibold whitespace-nowrap cursor-pointer select-none hover:text-brand-deep"
                                        >
                                            {label}
                                            {retireSort.key === key && (
                                                <span className="ml-1 text-ink-faint">{retireSort.dir === 1 ? '\u2191' : '\u2193'}</span>
                                            )}
                                        </th>
                                    ))}
                                    <th className="px-2 py-1.5" />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRetirements.map((r, i) => (
                                    <tr key={`${r.name}-${i}`} className="border-t border-line">
                                        <td className="px-2 py-1 font-mono">{r.name || '—'}</td>
                                        <td className="px-2 py-1">{r.subzone || '—'}</td>
                                        <td className="px-2 py-1 whitespace-nowrap">
                                            <span className="pill bg-brand-tint text-brand-deep mr-1">{r.initials || '??'}</span>
                                            <span className="text-ink-faint">{r.by_name || r.by}</span>
                                            {r.source === 'adopted' && (
                                                <span
                                                    className="pill bg-paper-2 text-ink-faint ml-1"
                                                    title="Recovered from the layer after the fact. The name is the account ArcGIS recorded as the last editor, which may not be who retired it."
                                                >
                                                    recovered
                                                </span>
                                            )}
                                            {r.source === 'note' && (
                                                <span
                                                    className="pill bg-paper-2 text-ink-faint ml-1"
                                                    title="Found by the note left in COMMENTS, not in Pioneer's ledger. Whoever is named here is the last person to edit the feature, which may not be who retired it."
                                                >
                                                    from note
                                                </span>
                                            )}
                                            {r.still_inactive === false && (
                                                <span className="pill bg-sle-tint text-sle ml-1" title="Pioneer retired this site but it is active again now">
                                                    active again
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1 whitespace-nowrap text-ink-faint">{r.at || '—'}</td>
                                        <td className="px-2 py-1">
                                            {r.reason || '—'}
                                            {r.symbology && r.symbology !== 'INACTIVE' && (
                                                <span className="pill bg-sle-tint text-sle ml-1">still drawn active</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => reactivate(r)}
                                                disabled={!!reviving[r.global_id]}
                                                title="Set active again, restore the map status, and remove Pioneer's note"
                                                className="btn-ghost text-xs !py-0.5"
                                            >
                                                {reviving[r.global_id] ? '…' : 'Reactivate'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {pickedList.length > 0 && (
                <SiteDeactivationPanel
                    sites={pickedList}
                    onClear={() => setPicked({})}
                    onDeactivated={dropPicked}
                />
            )}
        </div>
    );
}
